import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Truck, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const WEIGHT = [
  { id: "standard", label: "Normal (under 200 lb, e.g. couch, dresser, fridge)" },
  { id: "heavy", label: "Extremely heavy up to 200 lb (safe, tool chest) · +$100" },
  { id: "very_heavy", label: "200–300 lb · quoted individually" },
  { id: "over", label: "Over 300 lb · we can't take it" },
];

export default function TransportCalculator({ notes, onBook, disabled }) {
  const [form, setForm] = useState({ miles_one_way: 10, stairs: false, weight_class: "standard", item_count: 1 });
  const [fee, setFee] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      axios.post(`${API}/transport/calculate`, { ...form, miles_one_way: Number(form.miles_one_way) || 0 }).then(({ data }) => setFee(data)).catch(() => setFee(null));
    }, 250);
    return () => clearTimeout(t);
  }, [form]);

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/transport/calculate`, { ...form, miles_one_way: Number(form.miles_one_way) || 0, notes, save: true });
      onBook(data.quote);
      toast.success(`Exact fee locked — ${money(data.total)} · ref ${data.quote.ref_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create your quote"); } finally { setBusy(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=55423&destination=${encodeURIComponent("your address")}`;

  return (
    <div data-testid="transport-calculator" className="rounded-lg border-2 border-[#0C2E2B] bg-[#0C2E2B] text-white p-5 mb-5">
      <div className="flex items-center gap-2">
        <Calculator className="w-4 h-4 text-[#FFDF00]" />
        <h3 className="font-display font-black text-lg">Exact delivery fee — no photo needed</h3>
      </div>
      <p className="text-xs text-white/70 mt-1">Answer three questions and book at this price. Or scroll down and upload a photo if you'd rather we size it up.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div>
          <Label htmlFor="tc-miles" className="text-[10px] font-bold uppercase tracking-wider text-white/80">One-way miles from 55423 to the farther address</Label>
          <Input id="tc-miles" data-testid="transport-calc-miles-input" type="number" min="0" step="1" value={form.miles_one_way} onChange={(e) => set("miles_one_way", e.target.value)} className="mt-1 rounded-md bg-white text-[#0C2E2B] font-bold" />
          <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="transport-calc-maps-link" className="text-[10px] text-[#FFDF00] underline inline-flex items-center gap-1 mt-1">Check distance on Google Maps <ExternalLink className="w-3 h-3" /></a>
        </div>
        <div>
          <Label htmlFor="tc-weight" className="text-[10px] font-bold uppercase tracking-wider text-white/80">Heaviest item</Label>
          <select id="tc-weight" data-testid="transport-calc-weight-select" value={form.weight_class} onChange={(e) => set("weight_class", e.target.value)} className="mt-1 w-full h-10 rounded-md bg-white text-[#0C2E2B] text-sm font-bold px-2">
            {WEIGHT.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold cursor-pointer sm:col-span-2">
          <input type="checkbox" data-testid="transport-calc-stairs-checkbox" checked={form.stairs} onChange={(e) => set("stairs", e.target.checked)} className="w-4 h-4 accent-[#FFDF00]" />
          Stairs, elevator, long carry or tight access at either address (+$10)
        </label>
      </div>

      {fee && (
        <div className="mt-4 bg-white/10 rounded-md p-3" data-testid="transport-calc-result">
          {fee.lines.map((l, i) => (
            <div key={i} className="flex justify-between text-xs py-0.5"><span className="text-white/80">{l.label}</span><span className="font-mono-data font-bold">{money(l.amount)}</span></div>
          ))}
          <div className="flex justify-between items-baseline border-t border-white/20 mt-2 pt-2">
            <span className="text-xs uppercase tracking-widest font-bold text-white/80">{fee.exact ? "Your exact fee" : "Base fee"}</span>
            <span data-testid="transport-calc-total" className="font-display font-black text-3xl text-[#FFDF00]">{money(fee.total)}</span>
          </div>
          {fee.message && <p data-testid="transport-calc-message" className={`text-xs mt-2 ${fee.blocked ? "text-red-300" : "text-white/80"}`}>{fee.message}</p>}
          <Button data-testid="transport-calc-book-btn" onClick={book} disabled={busy || disabled || fee.blocked} className="mt-3 w-full h-11 rounded-md bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#E5C900] font-black">
            <Truck className="w-4 h-4 mr-2" /> {busy ? "Locking price…" : fee.blocked ? "Can't transport this item" : `Book delivery at ${money(fee.total)}`}
          </Button>
          <p className="text-[10px] text-white/60 mt-2">$25 deposit reserves your slot and comes off this price. Final fee confirmed before loading if details differ.</p>
        </div>
      )}
    </div>
  );
}
